import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { AUTH_TOKEN } from "constants/AuthConstant";
import FirebaseService from "services/FirebaseService";
import { AUTH_PREFIX_PATH } from "configs/AppConfig";
import {
  mapApiToRole,
  getDashboardPath,
  needsOnboardingAgreement,
  ONBOARDING_AGREEMENT_PATH,
} from "utils/roleHelpers";
import { clearLocationFromStorage } from "utils/locationService";
import apiHandle from "services/apiHandle";
import { disconnectSocket } from "services/socketService";
import { cleanupSocketEventHandlers } from "services/socketEventHandler";

export const initialState = {
  loading: false,
  message: "",
  showMessage: false,
  redirect: "",
  token: localStorage.getItem("auth_token") || null,
  userType: localStorage.getItem("USER_TYPE") || null, // 'truck_owner' or 'mechanic'
  userData: localStorage.getItem("USER_DATA")
    ? JSON.parse(localStorage.getItem("USER_DATA"))
    : null,
  // Onboarding completion flags
  isProfileCompleted:
    localStorage.getItem("IS_PROFILE_COMPLETED") === "true" || false,
  // Forgot password flow
  forgotPasswordUserId: null,
  forgotPasswordEmail: null,
};

export const signIn = createAsyncThunk(
  "auth/signIn",
  async (data, { rejectWithValue }) => {
    try {
      const response = await apiHandle("/auth/login", "POST", data);

      const token = response?.tokens?.access_token || null;
      const refreshToken = response?.tokens?.refresh_token || null;
      const userData = response?.user || response?.data || null;

      if (token) {
        localStorage.setItem("auth_token", token);
        localStorage.setItem("refresh_token", refreshToken);
        localStorage.setItem("USER_DATA", JSON.stringify(userData));
      }

      return {
        token,
        userData,
      };
    } catch (err) {
      const message = err?.message || "Login Error";
      return rejectWithValue(message);
    }
  },
);

export const signUp = createAsyncThunk(
  "auth/signUp",
  async (data, { rejectWithValue }) => {
    try {
      const response = await apiHandle("/auth/register", "POST", data);

      const userData = response?.data || response?.user || null;
      const token = response?.tokens?.access_token || null;
      const refreshToken = response?.tokens?.refresh_token || null;
      if (token) {
        localStorage.setItem("auth_token", token);
        localStorage.setItem("refresh_token", refreshToken);
      }

      return {
        token,
        userData,
      };
    } catch (err) {
      const message = err?.message || "Register Error";
      return rejectWithValue(message);
    }
  },
);

export const verifyOtp = createAsyncThunk(
  "auth/verifyOtp",
  async (otpCode, { rejectWithValue, dispatch }) => {
    try {
      const response = await apiHandle("/auth/verify-user-otp", "POST", {
        emcode: otpCode,
      });

      const updatedUser = response.user;
      const token = localStorage.getItem("auth_token");

      localStorage.setItem("USER_DATA", JSON.stringify(updatedUser));

      return { user: updatedUser, token };
    } catch (err) {
      const message = err?.message || "Invalid OTP";
      return rejectWithValue(message);
    }
  },
);

export const resendOtp = createAsyncThunk(
  "auth/resendOtp",
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiHandle("/auth/resend-otp", "GET");

      return response; // success message aayega
    } catch (err) {
      const message = err?.message || "Failed to resend OTP";
      return rejectWithValue(message);
    }
  },
);

export const verifyForgotPasswordOtp = createAsyncThunk(
  "auth/verifyForgotPasswordOtp",
  async ({ email, emcode }, { rejectWithValue }) => {
    try {
      const response = await apiHandle(
        "/auth/forget-password-otp-verify",
        "POST",
        { email, emcode },
      );

      return {
        userId: response.userId,
        resetToken: response.resetToken,
        message: response.message,
        email, // Pass email along for next step
      };
    } catch (err) {
      const message = err?.message || "Invalid OTP";
      return rejectWithValue(message);
    }
  },
);

export const uploadProfileImage = createAsyncThunk(
  "auth/uploadProfileImage",
  async (file, { rejectWithValue }) => {
    try {
      const formData = new FormData();
      formData.append("file", file);

      const response = await apiHandle(
        "/common/upload-files-s3",
        "POST",
        formData,
        { "Content-Type": "multipart/form-data" },
      );
      return response;
    } catch (err) {
      const message = err?.message || "Image upload failed";
      return rejectWithValue(message);
    }
  },
);

export const updateAvatar = createAsyncThunk(
  "auth/updateAvatar",
  async (avatarUrl, { rejectWithValue }) => {
    try {
      const response = await apiHandle("/user/update-avatar", "POST", {
        avatarUrl,
      });
      localStorage.setItem("USER_DATA", JSON.stringify(response.user));
      return response.user;
    } catch (err) {
      return rejectWithValue(err?.message || "Avatar update failed");
    }
  },
);

export const updateProfile = createAsyncThunk(
  "auth/updateProfile",
  async (profileData, { rejectWithValue }) => {
    try {
      // Format the data to match the API expectation
      const requestBody = {
        fullName: profileData.fullName || profileData.name,
        email: profileData.email,
        phoneNumber: profileData.phoneNumber || profileData.phone,
      };

      // Include avatar if provided
      if (profileData.avatar) {
        requestBody.avatar = profileData.avatar;
      }

      const response = await apiHandle(
        "/user/update-profile",
        "POST",
        requestBody,
      );

      // Update localStorage with the new user data
      const updatedUser = response.user || response.data;
      if (updatedUser) {
        localStorage.setItem("USER_DATA", JSON.stringify(updatedUser));
      }

      return updatedUser;
    } catch (err) {
      return rejectWithValue(err?.message || "Profile update failed");
    }
  },
);

export const deleteAccount = createAsyncThunk(
  "auth/deleteAccount",
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiHandle("/user/delete-account", "DELETE");

      return response;
    } catch (err) {
      return rejectWithValue(err?.message || "Account deletion failed");
    }
  },
);

export const changePassword = createAsyncThunk(
  "auth/changePassword",
  async (
    { currentPassword, newPassword, confirmPassword },
    { rejectWithValue },
  ) => {
    try {
      const response = await apiHandle("/user/change-password", "POST", {
        currentPassword,
        newPassword,
        confirmPassword,
      });

      return response;
    } catch (err) {
      const message = err?.message || "Failed to change password";
      return rejectWithValue(message);
    }
  },
);

export const updateBusinessProfile = createAsyncThunk(
  "auth/updateBusinessProfile",
  async (businessProfileData, { rejectWithValue }) => {
    try {
      const response = await apiHandle(
        "/business/update-business-profile",
        "POST",
        businessProfileData,
      );

      // Update localStorage with the new user data
      const updatedUser = response.user || response.data;
      if (updatedUser) {
        localStorage.setItem("USER_DATA", JSON.stringify(updatedUser));
      }

      return response;
    } catch (err) {
      const message = err?.message || "Failed to update business profile";
      return rejectWithValue(message);
    }
  },
);

export const forgotPassword = createAsyncThunk(
  "auth/forgotPassword",
  async (email, { rejectWithValue }) => {
    try {
      const response = await apiHandle("/auth/forget-password-email", "POST", {
        email,
      });

      return response;
    } catch (err) {
      const message = err?.message || "Failed to send reset email";
      return rejectWithValue(message);
    }
  },
);

export const authMe = createAsyncThunk(
  "auth/authMe",
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiHandle("/auth/auth-me", "GET");

      const userData = response?.data || response?.user || null;
      if (userData) {
        localStorage.setItem("USER_DATA", JSON.stringify(userData));
      }

      return userData;
    } catch (err) {
      const message = err?.message || "Failed to fetch user data";
      return rejectWithValue(message);
    }
  },
);

export const resetPassword = createAsyncThunk(
  "auth/resetPassword",
  async (
    { email, password, confirmPassword, resetToken },
    { rejectWithValue },
  ) => {
    try {
      const response = await apiHandle("/auth/reset-password", "POST", {
        email,
        password,
        confirmPassword,
        resetToken,
      });

      return response;
    } catch (err) {
      const message = err?.message || "Failed to reset password";
      return rejectWithValue(message);
    }
  },
);

export const changeRoleToFleetManager = createAsyncThunk(
  "auth/changeRoleToFleetManager",
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiHandle("/user/toggle-role", "PATCH");
      // apiHandle already returns res.data, so response = { success, message, data: user }
      const user = response?.data || response?.user || response;
      const role = mapApiToRole(user?.role);

      // Persist to localStorage
      localStorage.setItem("USER_DATA", JSON.stringify(user));
      localStorage.setItem("USER_TYPE", role);

      return { user, role };
    } catch (error) {
      return rejectWithValue(error?.message || "Failed to change role");
    }
  },
);

export const signOut = createAsyncThunk("auth/signOut", async () => {
  // Cleanup socket connection
  cleanupSocketEventHandlers();
  disconnectSocket();

  // Clear ALL localStorage data on logout
  localStorage.clear();

  return true;
});

export const logOut = createAsyncThunk(
  "auth/logOut",
  async (_, { dispatch }) => {
    // Cleanup socket connection
    cleanupSocketEventHandlers();
    disconnectSocket();

    // Clear ALL localStorage data on logout
    localStorage.clear();

    return true;
  },
);

export const signInWithGoogle = createAsyncThunk(
  "auth/signInWithGoogle",
  async (_, { rejectWithValue }) => {
    const response = await FirebaseService.signInGoogleRequest();
    if (response.user) {
      const token = response.user.refreshToken;
      localStorage.setItem(AUTH_TOKEN, response.user.refreshToken);
      return token;
    } else {
      return rejectWithValue(response.message?.replace("Firebase: ", ""));
    }
  },
);

export const signInWithFacebook = createAsyncThunk(
  "auth/signInWithFacebook",
  async (_, { rejectWithValue }) => {
    const response = await FirebaseService.signInFacebookRequest();
    if (response.user) {
      const token = response.user.refreshToken;
      localStorage.setItem(AUTH_TOKEN, response.user.refreshToken);
      return token;
    } else {
      return rejectWithValue(response.message?.replace("Firebase: ", ""));
    }
  },
);

export const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    authenticated: (state, action) => {
      state.loading = false;
      state.redirect = "/";
      state.token = action.payload;
    },
    setUserType: (state, action) => {
      state.userType = action.payload;
      localStorage.setItem("USER_TYPE", action.payload);
    },
    showAuthMessage: (state, action) => {
      state.message = action.payload;
      state.showMessage = true;
      state.loading = false;
    },
    hideAuthMessage: (state) => {
      state.message = "";
      state.showMessage = false;
    },
    signOutSuccess: (state) => {
      state.token = null;
      state.loading = false;
      state.userType = "";
      state.userData = null;
      state.isProfileCompleted = false;
      localStorage.removeItem("USER_TYPE");
      localStorage.removeItem("USER_DATA");
      localStorage.removeItem("auth_token");
      localStorage.removeItem("IS_PROFILE_COMPLETED");
    },
    showLoading: (state) => {
      state.loading = true;
    },
    signInSuccess: (state, action) => {
      state.loading = false;
      state.token = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(signIn.pending, (state) => {
        state.loading = true;
      })

      .addCase(signIn.rejected, (state, action) => {
        state.message = action.payload;
        state.showMessage = true;
        state.loading = false;
      })
      .addCase(signOut.fulfilled, (state) => {
        state.loading = false;
        state.token = null;
        state.userData = null;
        state.userType = null;
        state.redirect = "/auth/login";
      })
      .addCase(signOut.rejected, (state) => {
        state.loading = false;
        state.token = null;
        state.userData = null;
        state.userType = null;
        state.redirect = "/auth/login";
      })
      .addCase(signUp.pending, (state) => {
        state.loading = true;
      })

      .addCase(signUp.fulfilled, (state, action) => {
        state.loading = false;
        state.token = action.payload.token;
        state.userType = mapApiToRole(action.payload.userData?.role);
        state.userData = action.payload.userData;
        localStorage.setItem("USER_TYPE", state.userType);
        localStorage.setItem("USER_DATA", JSON.stringify(state.userData));

        state.redirect = `${AUTH_PREFIX_PATH}/otp-input`;
        state.isProfileCompleted = false;
        localStorage.setItem("IS_PROFILE_COMPLETED", "false");
      }) // Sign in
      .addCase(signIn.fulfilled, (state, action) => {
        state.loading = false;
        state.token = action.payload.token;
        state.userData = action.payload.userData;

        // Role from API se le lo
        state.userType = mapApiToRole(action.payload.userData?.role);

        localStorage.setItem("USER_TYPE", state.userType);
        if (state.userData) {
          localStorage.setItem("USER_DATA", JSON.stringify(state.userData));
        }

        // Check onboarding completion flags
        const isVerified = action.payload.userData?.verified || false;

        const isProfileCompleteFromStorage =
          localStorage.getItem("IS_PROFILE_COMPLETED") === "true";
        const hasBasicInfo =
          action.payload.userData?.firstName &&
          action.payload.userData?.firstName.trim() !== "";

        const isProfileComplete =
          isProfileCompleteFromStorage || (isVerified && hasBasicInfo);

        // Update state and localStorage
        state.isProfileCompleted = isProfileComplete;
        if (isProfileComplete && !isProfileCompleteFromStorage) {
          // Save for old users who don't have this flag
          localStorage.setItem("IS_PROFILE_COMPLETED", "true");
        }

        // Redirect based on onboarding completion for ALL roles
        if (!isVerified) {
          // Not verified yet - send to OTP verification
          state.redirect = `${AUTH_PREFIX_PATH}/otp-input`;
        } else if (!isProfileComplete) {
          // Verified but profile not completed - send to profile setup
          state.redirect = `${AUTH_PREFIX_PATH}/profile-setup`;
        } else if (needsOnboardingAgreement(action.payload.userData)) {
          // Carrier who hasn't signed the DocuSign agreement yet.
          state.redirect = ONBOARDING_AGREEMENT_PATH;
        } else {
          // Fully onboarded - send to role-specific dashboard
          state.redirect = getDashboardPath(state.userType);
        }
      })

      .addCase(signUp.rejected, (state, action) => {
        state.message = action.payload;
        state.showMessage = true;
        state.loading = false;
      })

      // OTP Verification
      .addCase(verifyOtp.pending, (state) => {
        state.loading = true;
      })
      .addCase(verifyOtp.fulfilled, (state, action) => {
        state.loading = false;
        state.showMessage = true;
        state.message = "Verified Successfully!";

        // Update userData with verified status
        if (action.payload?.user) {
          state.userData = action.payload.user;
          localStorage.setItem("USER_DATA", JSON.stringify(state.userData));
        }

        if (state.isProfileCompleted) {
          state.redirect = needsOnboardingAgreement(state.userData)
            ? ONBOARDING_AGREEMENT_PATH
            : getDashboardPath(state.userType);
        } else {
          state.redirect = `${AUTH_PREFIX_PATH}/profile-setup`;
        }
      })
      .addCase(verifyOtp.rejected, (state, action) => {
        state.loading = false;
        state.showMessage = true;
        state.message = action.payload || "Verification failed";
      })
      // Resend OTP
      .addCase(resendOtp.pending, (state) => {
        state.loading = true;
      })
      .addCase(resendOtp.fulfilled, (state, action) => {
        state.loading = false;
        state.showMessage = true;
        state.message =
          action.payload?.message || "New OTP sent to your email!";
      })
      .addCase(resendOtp.rejected, (state, action) => {
        state.loading = false;
        state.showMessage = true;
        state.message = action.payload || "Failed to resend OTP";
      })

      // Verify Forgot Password OTP
      .addCase(verifyForgotPasswordOtp.pending, (state) => {
        state.loading = true;
      })
      .addCase(verifyForgotPasswordOtp.fulfilled, (state, action) => {
        state.loading = false;
        state.showMessage = true;
        state.message = "Account verified, You can reset password!";
        // Store userId and email for password reset step
        state.forgotPasswordUserId = action.payload.userId;
        state.forgotPasswordEmail = action.payload.email;
      })
      .addCase(verifyForgotPasswordOtp.rejected, (state, action) => {
        state.loading = false;
        state.showMessage = true;
        state.message = action.payload || "Invalid OTP";
      })

      // Upload Image
      .addCase(uploadProfileImage.pending, (state) => {
        state.loading = true;
      })
      .addCase(uploadProfileImage.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(uploadProfileImage.rejected, (state, action) => {
        state.loading = false;
        state.message = action.payload;
        state.showMessage = true;
      })

      // Update Avatar
      .addCase(updateAvatar.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateAvatar.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(updateAvatar.rejected, (state, action) => {
        state.loading = false;
        state.message = action.payload;
        state.showMessage = true;
      })

      // Update Profile
      .addCase(updateProfile.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateProfile.fulfilled, (state, action) => {
        state.loading = false;
        state.userData = action.payload; // Update userData with new profile data

        // Update localStorage with new user data
        if (state.userData) {
          localStorage.setItem("USER_DATA", JSON.stringify(state.userData));
        }

        // Mark onboarding as completed after profile setup
        state.isProfileCompleted = true;
        localStorage.setItem("IS_PROFILE_COMPLETED", "true");

        // Redirect mechanic to business-profile-setup, others complete onboarding
        if (state.userType === "mechanic") {
          state.redirect = `${AUTH_PREFIX_PATH}/business-profile-setup`;
        }

        state.showMessage = true;
        state.message = "Profile updated successfully!";
      })
      .addCase(updateProfile.rejected, (state, action) => {
        state.loading = false;
        state.message = action.payload;
        state.showMessage = true;
      })

      // Update Business Profile
      .addCase(updateBusinessProfile.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateBusinessProfile.fulfilled, (state, action) => {
        state.loading = false;
        state.userData = action.payload.user;

        // Update localStorage with new user data
        if (state.userData) {
          localStorage.setItem("USER_DATA", JSON.stringify(state.userData));
        }

        state.showMessage = true;
        state.message =
          action.payload?.message || "Business profile updated successfully!";
      })
      .addCase(updateBusinessProfile.rejected, (state, action) => {
        state.loading = false;
        state.message = action.payload;
        state.showMessage = true;
      })

      // Change Password
      .addCase(changePassword.pending, (state) => {
        state.loading = true;
      })
      .addCase(changePassword.fulfilled, (state, action) => {
        state.loading = false;
        state.showMessage = true;
        state.message =
          action.payload?.message || "Password changed successfully!";
      })
      .addCase(changePassword.rejected, (state, action) => {
        state.loading = false;
        state.showMessage = true;
        state.message = action.payload || "Failed to change password";
      })

      // Forgot Password
      .addCase(forgotPassword.pending, (state) => {
        state.loading = true;
      })
      .addCase(forgotPassword.fulfilled, (state, action) => {
        state.loading = false;
        state.showMessage = true;
        state.message =
          action.payload?.message || "Reset password link sent to your email!";
      })
      .addCase(forgotPassword.rejected, (state, action) => {
        state.loading = false;
        state.showMessage = true;
        state.message = action.payload || "Failed to send reset email";
      })
      // Reset Password
      .addCase(resetPassword.pending, (state) => {
        state.loading = true;
      })
      .addCase(resetPassword.fulfilled, (state, action) => {
        state.loading = false;
        state.showMessage = true;
        state.message = "Password changed successfully!";
      })
      .addCase(resetPassword.rejected, (state, action) => {
        state.loading = false;
        state.showMessage = true;
        state.message = action.payload || "Password reset failed";
      })

      // Auth Me
      .addCase(authMe.pending, (state) => {
        state.loading = true;
      })
      .addCase(authMe.fulfilled, (state, action) => {
        state.loading = false;
        state.userData = action.payload;

        // Sync profile completion status from localStorage
        const isProfileComplete =
          localStorage.getItem("IS_PROFILE_COMPLETED") === "true";
        state.isProfileCompleted = isProfileComplete;
      })
      .addCase(authMe.rejected, (state, action) => {
        state.loading = false;
        state.message = action.payload;
        state.showMessage = true;
      })

      .addCase(signInWithGoogle.pending, (state) => {
        state.loading = true;
      })
      .addCase(signInWithGoogle.fulfilled, (state, action) => {
        state.loading = false;
        state.redirect = "/";
        state.token = action.payload;
      })
      .addCase(signInWithGoogle.rejected, (state, action) => {
        state.message = action.payload;
        state.showMessage = true;
        state.loading = false;
      })
      .addCase(signInWithFacebook.pending, (state) => {
        state.loading = true;
      })
      .addCase(signInWithFacebook.fulfilled, (state, action) => {
        state.loading = false;
        state.redirect = "/";
        state.token = action.payload;
      })
      .addCase(signInWithFacebook.rejected, (state, action) => {
        state.message = action.payload;
        state.showMessage = true;
        state.loading = false;
      })

      // Change Role (toggle between OWNER and FLEET_MANAGER)
      .addCase(changeRoleToFleetManager.pending, (state) => {
        state.loading = true;
      })
      .addCase(changeRoleToFleetManager.fulfilled, (state, action) => {
        state.loading = false;
        state.userData = action.payload.user;
        state.userType = action.payload.role;
        state.showMessage = true;
        state.message = "Role changed successfully!";
        state.redirect = getDashboardPath(action.payload.role);
      })
      .addCase(changeRoleToFleetManager.rejected, (state, action) => {
        state.loading = false;
        state.showMessage = true;
        state.message = action.payload || "Failed to change role";
      });
  },
});

export const {
  authenticated,
  setUserType,
  showAuthMessage,
  hideAuthMessage,
  signOutSuccess,
  showLoading,
  signInSuccess,
} = authSlice.actions;

export default authSlice.reducer;
